/**
 * Per-resource capability grants (EHQ-120 / EHQ-187).
 *
 * A grant lifts one narrow capability above a member's role baseline — "may run
 * *this* routine" — without granting `Write` over the whole project. Grants only
 * ever add authority; restriction is expressed by moving a membership's scope
 * axis to `Granted` and then granting back the specific resources.
 *
 * Server shapes are `rename_all = "camelCase"`; see
 * `server/src/api/projects/grant_handlers.rs`.
 */

/**
 * The capabilities that may be granted, by name.
 *
 * The server takes the *name* on write and returns a numeric `permissionId` on
 * read, so {@link PERMISSION_ID} maps between the two. Only the pairs the server
 * allowlists appear here — `GrantTarget::accepts_permission` refuses anything
 * else, and offering an ungrantable pair in the UI would only produce a 400.
 */
export type GrantPermission =
  | 'RunRoutine'
  | 'ApproveReview'
  | 'ViewResource'
  | 'EditResource'
  | 'PublishVersion';

/**
 * `Permission::Admin`'s id in the server's catalog.
 *
 * Not a grantable capability, so it is deliberately outside
 * {@link PERMISSION_ID} — but a role holding it is exempt from scoping, and
 * that is how the server decides: `ProjectUser::role_is_admin` checks for this
 * permission on the role, never for a particular role id.
 */
export const ADMIN_PERMISSION_ID = 6;

/**
 * Numeric ids from the server's `permission` catalog, for reading
 * {@link ProjectCapabilityGrant.permissionId} back into a name.
 */
export const PERMISSION_ID: Record<GrantPermission, number> = {
  RunRoutine: 8,
  ApproveReview: 9,
  ViewResource: 11,
  EditResource: 12,
  PublishVersion: 13,
};

/** Reverse of {@link PERMISSION_ID}. */
export const PERMISSION_BY_ID: ReadonlyMap<number, GrantPermission> = new Map(
  (Object.entries(PERMISSION_ID) as [GrantPermission, number][]).map(([name, id]) => [id, name])
);

/** The resource kinds a grant can point at. */
export type GrantResourceType = 'Routine' | 'Workflow' | 'ApiEndpoint' | 'Project';

/**
 * What a grant points at, in the shape the wire uses — a tagged union, so a
 * request cannot describe a row the server's CHECK constraint would reject.
 *
 * Routines key on `resourceId` because routine ids survive publishing. Workflows
 * key on their **permanent** name — never a display name — because `version
 * create` copies workflows with fresh ids, so an id-keyed grant would evaporate
 * on the next publish.
 */
export type GrantTarget =
  | { resourceType: 'Routine'; resourceId: number }
  | { resourceType: 'Workflow'; resourceName: string }
  | { resourceType: 'ApiEndpoint'; resourceName: string }
  | { resourceType: 'Project' };

/** A grant row as the server returns it. */
export interface ProjectCapabilityGrant {
  grantId: number;
  projectId: number;
  userId: number;
  permissionId: number;
  resourceType: GrantResourceType;
  /** Set only for `Routine`, whose ids survive publishing. */
  resourceId?: number | null;
  /** Set only for `Workflow` / `ApiEndpoint`, which key on a permanent name. */
  resourceName?: string | null;
  /** Who delegated this authority — the first question asked during an audit. */
  grantedBy: number;
  disabled: boolean;
  createdAt: string;
  modifiedAt: string;
  version: number;
}

/** One grant to issue. */
export type CreateGrantRequest = {
  userId: number;
  permission: GrantPermission;
} & GrantTarget;

/** A check-all in one round trip. */
export interface CreateGrantsRequest {
  grants: CreateGrantRequest[];
}

/**
 * Whether a membership sees/edits everything its role allows, or only granted
 * resources.
 */
export type AccessScope = 'All' | 'Granted';

/** Both scope axes, which the server writes together. */
export interface MemberScopes {
  viewScope: AccessScope;
  editScope: AccessScope;
}

/**
 * The caller's own effective view scoping.
 *
 * Not derivable from the grant rows alone: an admin is exempt from scoping even
 * when grant rows exist for them, and a member at `Granted` with no grants sees
 * nothing — where an empty grant list would otherwise read as "unrestricted".
 */
export interface MyVisibility {
  viewScope: AccessScope;
  /** Whether the role holds `Admin`, which exempts it from scoping entirely. */
  isAdmin: boolean;
  /**
   * Whether view scoping actually restricts this caller. When `false`,
   * {@link visibleWorkflows} is absent and every workflow is visible.
   */
  restricted: boolean;
  /**
   * Permanent names of the workflows this caller may see. Absent when
   * unrestricted — an empty list means "nothing visible", so the two states do
   * not share a representation.
   */
  visibleWorkflows?: string[];
}
